import { type ComponentProps } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button-v25-3/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu-next';
import AddIcon from '@atlaskit/icon/core/add';
import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';

import { JsmPlaygroundSearchField } from '../components/jsm-playground-search-field';

import { engineeringMessages as messages } from './jsm-playground-engineering-messages';

type ButtonIcon = NonNullable<ComponentProps<typeof Button>['iconAfter']>;
type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const AddViewButtonIcon: IconButtonIcon = () => <AddIcon label="" size="medium" />;
const FilterChevronIcon: ButtonIcon = () => <ChevronDownIcon label="" size="medium" />;
const MoreActionsIcon: IconButtonIcon = () => <ShowMoreHorizontalIcon label="" size="medium" />;

export const JsmPlaygroundEngineeringToolbar = () => {
	const intl = useIntl();

	return (
		<>
			<div css={pageHeaderStyles}>
				<div aria-level={3} css={pageTitleStyles} role="heading">
					<FormattedMessage {...messages.incomingRequests} />
				</div>
				<div css={viewBarStyles}>
					<div css={viewTabsStyles}>
						<button aria-pressed css={activeViewStyles} type="button">
							<ListBulletedIcon label="" size="small" />
							<FormattedMessage {...messages.list} />
						</button>
					</div>
					<div css={viewBarRemainderStyles}>
						<span aria-hidden css={viewBarRuleStyles} />
						<span css={addViewDropdownStyles}>
							<DropdownMenu
								placement="bottom-start"
								shouldRenderToParent
								testId="engineering-add-view"
								trigger={({ triggerRef, ...triggerProps }) => (
									<IconButton
										{...triggerProps}
										ref={triggerRef}
										appearance="subtle"
										icon={AddViewButtonIcon}
										label={intl.formatMessage(messages.addView)}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem
										elemBefore={<CalendarIcon color="currentColor" label="" size="small" />}
									>
										<FormattedMessage {...messages.calendar} />
									</DropdownItem>
									<DropdownItem
										elemBefore={<BoardIcon color="currentColor" label="" size="small" />}
									>
										<FormattedMessage {...messages.board} />
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</span>
					</div>
				</div>
			</div>

			<div css={toolbarStyles}>
				<div css={searchStyles}>
					<JsmPlaygroundSearchField
						label={intl.formatMessage(messages.searchWork)}
						placeholder={intl.formatMessage(messages.searchWork)}
					/>
				</div>
				{[messages.requestType, messages.status, messages.assignee].map((message) => (
					<span aria-hidden css={inertControlStyles} key={message.id}>
						<Button iconAfter={FilterChevronIcon} tabIndex={-1}>
							{intl.formatMessage(message)}
						</Button>
					</span>
				))}
				<span aria-hidden css={toolbarActionsStyles}>
					<Button tabIndex={-1}>
						<FormattedMessage {...messages.triage} />
					</Button>
					<IconButton
						icon={MoreActionsIcon}
						label={intl.formatMessage(messages.moreActions)}
						tabIndex={-1}
					/>
				</span>
			</div>
		</>
	);
};

const pageHeaderStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	// The prototype keeps an empty breadcrumb row above the title, so the column's 8px gap sits
	// between that row and the title: 24px padding + 8px gap.
	paddingBlockStart: `calc(${token('space.300')} + ${token('space.100')})`,
	paddingInline: token('space.300'),
});
const pageTitleStyles = css({
	marginBlock: 0,
	fontFamily: token('font.family.body'),
	fontSize: '20px',
	fontWeight: 600,
	// The prototype's title is a bare span with no line-height of its own.
	lineHeight: 'normal',
});
const viewBarStyles = css({ display: 'flex', alignItems: 'flex-end' });
const viewTabsStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	alignItems: 'center',
});
// Drawn to the ADS tab the prototype uses: a 6px/8px tab whose label span adds another 8px inline,
// the strip pulled left by the tab's own inline padding, a 1px baseline under the whole tab and the
// 2px selected indicator inset to the tab's padding.
const activeViewStyles = css({
	position: 'relative',
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.075'),
	marginInlineStart: `calc(-1 * ${token('space.100')})`,
	paddingBlock: token('space.075'),
	paddingInline: token('space.200'),
	borderWidth: 0,
	backgroundColor: 'transparent',
	color: token('color.text.selected'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
	cursor: 'pointer',
	'&::before': {
		content: '""',
		position: 'absolute',
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		height: token('border.width', '1px'),
		backgroundColor: token('color.border'),
	},
	'&::after': {
		content: '""',
		position: 'absolute',
		insetBlockEnd: 0,
		insetInlineStart: token('space.100'),
		insetInlineEnd: token('space.100'),
		height: token('border.width.selected', '2px'),
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.border.selected'),
	},
	'&:focus-visible': {
		outlineWidth: token('border.width.focused', '2px'),
		outlineStyle: 'solid',
		outlineColor: token('color.border.focused'),
		outlineOffset: `calc(-1 * ${token('border.width.focused', '2px')})`,
	},
});
const viewBarRemainderStyles = css({
	position: 'relative',
	display: 'flex',
	minWidth: 0,
	flex: '1 1 auto',
	alignSelf: 'stretch',
	alignItems: 'center',
	paddingInlineStart: token('space.050'),
});
const viewBarRuleStyles = css({
	position: 'absolute',
	insetBlockEnd: 0,
	insetInlineStart: 0,
	insetInlineEnd: 0,
	height: token('border.width', '1px'),
	backgroundColor: token('color.border'),
});
const addViewDropdownStyles = css({
	position: 'relative',
	display: 'inline-flex',
	alignItems: 'center',
});
const toolbarStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingBlock: token('space.200'),
	paddingInline: token('space.300'),
});
const searchStyles = css({ display: 'flex', width: '220px', flexShrink: 0, alignItems: 'center' });
const inertControlStyles = css({ display: 'inline-flex', pointerEvents: 'none' });
const toolbarActionsStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	alignItems: 'center',
	gap: token('space.050'),
	marginInlineStart: 'auto',
	pointerEvents: 'none',
});
